"use client";
import React, { useState } from "react";
import { useGlobalStore } from "~/stores/store-provider";
import { LuX, LuSparkles } from "react-icons/lu";

interface AIEditPanelProps {
  sessionId: string;
}

const AIEditPanel = ({ sessionId }: AIEditPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [userPrompt, setUserPrompt] = useState("");
  const [error, setError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const { generateDocumentWithAI } = useGlobalStore((state: any) => state);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedPrompt = userPrompt.trim();
    if (!trimmedPrompt) {
      setError("Please provide instructions for the AI");
      return;
    }

    try {
      setIsGenerating(true);
      setError("");

      // Call the existing generateDocumentWithAI function
      await generateDocumentWithAI(sessionId, trimmedPrompt);

      // Reset and close
      setUserPrompt("");
      setIsExpanded(false);
    } catch (error) {
      console.error("Failed to generate with AI:", error);
      setError("Failed to process your request. Please try again.");
      setIsGenerating(false);
    }
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserPrompt(e.target.value);
    if (error) setError("");
  };

  const handleClose = () => {
    setUserPrompt("");
    setError("");
    setIsGenerating(false);
    setIsExpanded(false);
  };

  const toggleExpanded = () => {
    if (isExpanded) {
      handleClose();
    } else {
      setIsExpanded(true);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Floating Button */}
      {!isExpanded && (
        <div className="tooltip" data-tip="AI Assist">
          <div className="relative p-[2px] bg-gradient-to-r from-sky-500 to-pink-500 rounded-full transition-all duration-[0.4s] ease-in-out group">
            <div className="absolute inset-0 m-auto rounded-full -z-10 blur-0 transition-[filter] duration-[0.4s] ease-in-out group-hover:bg-gradient-to-r group-hover:from-sky-500 group-hover:to-pink-500 group-hover:blur-[1.2rem] group-active:blur-[0.2rem]"></div>
            <button
              onClick={toggleExpanded}
              className="text-[1.4em] py-[0.4em] px-[0.4em] rounded-full border-none bg-black text-white cursor-pointer shadow-[2px_2px_3px_#000000b4] w-12 h-12 flex items-center justify-center"
              title="AI Document Assistant"
            >
              <LuSparkles className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Expanded Search Bar */}
      <div
        className={`absolute bottom-0 right-0 bg-base-100 rounded-2xl shadow-2xl border border-base-200 p-4 w-80 max-w-[calc(100vw-3rem)] transition-all duration-300 origin-bottom-right ${
          isExpanded
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <form onSubmit={handleGenerate} className="space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LuSparkles className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-lg">AI Assistant</h3>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-sm btn-circle btn-ghost"
              disabled={isGenerating}
            >
              <LuX className="w-4 h-4" />
            </button>
          </div>

          {/* Input Area */}
          <div>
            <div className="mb-2 overflow-hidden">
              <span className="text-sm text-base-content/80 block text-wrap">
                What would you like AI to do with this document?
              </span>
            </div>
            <textarea
              autoFocus
              value={userPrompt}
              onChange={handlePromptChange}
              placeholder="e.g., Make it more concise, add an executive summary, improve clarity..."
              className={`textarea textarea-bordered w-full text-sm ${
                error ? "textarea-error" : ""
              }`}
              rows={2}
              disabled={isGenerating}
            />
            {error && (
              <label className="label">
                <span className="label-text-alt text-error text-xs">
                  {error}
                </span>
              </label>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-ghost btn-sm"
              disabled={isGenerating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={!userPrompt.trim() || isGenerating}
            >
              {isGenerating ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  Generating...
                </>
              ) : (
                <>
                  Generate
                  <LuSparkles className="w-3 h-3 ml-1" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AIEditPanel;
